/**
 * @file This file contains configuration for random, unusual NPC actions.
 * These are rare, non-standard behaviors intended to add variety and serve
 * as a basis for a potential "spot the oddity" game mechanic.
 * @module npcRandomActionsConfig
 */

/**
 * @typedef {Object} RandomNpcAction
 * @property {string} key - A unique identifier for the action.
 * @property {'effect' | 'movement'} type - The type of zany action.
 * @property {number} probability - The chance of this specific action occurring if a zany action is triggered.
 * @property {string[]} [effects] - For 'effect' type: A list of temporary effect keys to choose from.
 * @property {number} [duration] - For 'effect' type: The duration of the visual effect.
 * @property {number} [segments] - For 'movement' type: The number of steps in the movement pattern.
 * @property {number} [spread] - For 'movement' type: The perpendicular distance for zig-zagging.
 * @property {number} [repeats] - For 'movement' type: The number of times to repeat a back-and-forth motion.
 */

export const npcRandomActionsConfig = {
     /**
     * An array of possible random actions. The probabilities within this array
     * determine which action is chosen if the global probability check passes.
     * The sum of probabilities here should ideally be 1.0.
     * @type {RandomNpcAction[]}
     */
    actions: [
        {
            key: 'show_random_emotion',
            type: 'effect',
            probability: 0.3, // 30% chance of this zany action
            effects: ['zany_thought', 'dust', 'cleaned', 'megaMerge'],
            duration: 5000
        },
        {
            key: 'zig_zag_path',
            type: 'movement',
            probability: 0.2, // 20% chance
            segments: 4
        },
        {
            key: 'stare_at_player',
            type: 'effect',
            probability: 0.1, // 10% chance
            duration: 30000, // 10 seconds
            effects: [] // No emoji effect, just the animation
        },
        {
            key: 'square_dance',
            type: 'movement',
            probability: 0.2, // 20% chance
            repeats: 5 // The number of times to trace the square
        },
        {
            key: 'fast_move',
            type: 'movement',
            probability: 0.2, // 20% chance
            duration: 20, // Lasts for 50 ticks (~10 seconds)
            speedMultiplier: 5 // 3x faster than normal
        }
    ]
};